/**
 * 管理后台的页面分发与外框。
 *
 * 背景：PRD 7 章管理后台页面：总览、账号、模型与价格、上游渠道、订单与商品、审计日志。
 * 设计意图：页面路径避开管理接口前缀（/admin/、/control/admin/、/billing/admin/），入口负载均衡把其余路径
 *   都交给管理后台的 index.html；带 ID 的详情页用 matchPath 匹配。
 * 关键约束：页面组件只在 status === 'in' 时渲染，页面里 account 一定存在。
 */
import { IconLogout } from '@tabler/icons-react'
import { Fragment, useEffect, type ReactNode } from 'react'
import { A } from '@ap/i18n/admin'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Badge, BrandMark, Loading, LocaleSelect, matchPath, navigate, Offline, useRoute } from '@ap/ui'
import { AccountDetail } from './pages/AccountDetail.tsx'
import { Dashboard } from './pages/Dashboard.tsx'
import { Login } from './pages/Login.tsx'
import { OrderDetail } from './pages/Payments.tsx'
import { PAGES, SECTIONS, type SectionId } from './registry.tsx'
import { goAfterLogin, loginUrl, useAdmin } from './session.tsx'

/** 带参数的详情页（不进侧栏）。 */
const DETAIL_ROUTES: [string, (p: Record<string, string>) => ReactNode][] = [
  ['/accounts/:id', (p) => <AccountDetail id={p.id} />],
  ['/payments/orders/:id', (p) => <OrderDetail id={p.id} />],
]

function render(path: string): ReactNode {
  for (const [pattern, el] of DETAIL_ROUTES) {
    const m = matchPath(pattern, path)
    if (m) return el(m)
  }
  const page = PAGES.find((p) => matchPath(p.path, path))
  const Page = page?.component ?? Dashboard
  return <Page />
}

export function App() {
  const { path } = useRoute()
  const { status, retry } = useAdmin()

  useEffect(() => {
    if (status === 'out' && path !== '/login') navigate(loginUrl(), { replace: true })
    if (status === 'in' && path === '/login') goAfterLogin(new URLSearchParams(window.location.search).get('return_to'))
  }, [status, path])

  if (status === 'loading') return <Loading />
  if (status === 'offline') return <Offline onRetry={retry} />
  if (path === '/login') return <Login />
  if (status !== 'in') return <Loading />
  return <Shell path={path}>{render(path)}</Shell>
}

/** 与 Alice gateway_admin.html 一致的外框：左侧分组导航、顶栏标题、页面主体。 */
function Shell({ path, children }: { path: string; children: ReactNode }) {
  const { t } = useI18n()
  const { account, logout } = useAdmin()
  const active = PAGES.find((p) => (p.path === '/' ? path === '/' : path === p.path || path.startsWith(`${p.path}/`)))
  const sections = Object.keys(SECTIONS) as SectionId[]
  return (
    <div className="alice-admin" style={{ display: 'flex', width: '100%' }}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <BrandMark size={28} />
          <div>
            <span className="sidebar-brand-text brand-wordmark">Asterove</span>
            <span className="sidebar-brand-sub">Provider Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {sections.map((sec) => (
            <Fragment key={sec}>
              <div className="nav-section">{t(SECTIONS[sec])}</div>
              {PAGES.filter((p) => p.section === sec).map((p) => {
                const Icon = p.icon
                return (
                  <div
                    key={p.id}
                    role="link"
                    tabIndex={0}
                    className={`nav-item${active?.id === p.id ? ' active' : ''}`}
                    onClick={() => navigate(p.path)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(p.path)}
                  >
                    <Icon size={16} />
                    {t(p.label)}
                  </div>
                )
              })}
            </Fragment>
          ))}
        </nav>
        <div className="sidebar-footer">
          <a role="button" tabIndex={0} onClick={logout}>
            <IconLogout size={16} />
            {t(C.logout)}
          </a>
        </div>
      </aside>
      <div className="main">
        <div className="page-topbar">
          <span className="page-topbar-title">{active ? t(active.label) : t(A.nav.title)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LocaleSelect />
            {account?.operator_role && <Badge tone="accent">{t(A.role[account.operator_role])}</Badge>}
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{account?.email}</span>
          </div>
        </div>
        <div className="page-body">{children}</div>
      </div>
    </div>
  )
}
